import __includes from 'lodash/includes';
import __isFunction from 'lodash/isFunction';
import { useRouter } from 'next/router';
import { createContext, useEffect, useMemo, useState } from 'react';
import LoginDty from 'components/LoginDty/LoginDty';
import { isEditModeActive } from '@portal-internet/bff';
import { isValidString } from 'utils/utils';

export const UserDelivertyContext = createContext();

const defaultUserData = {
    token: null,
    usuari: null
};

const _parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const payloadObject = JSON.parse(jsonPayload);
        return payloadObject;
    } catch (error) {
        return null;
    }
};

const _getUserDataFromLocalStorage = () => {
    if (typeof window !== 'undefined') {
        const tokenString = localStorage.getItem('token');
        if (tokenString) {
            const userData = JSON.parse(tokenString);
            return userData;
        }
    }
    return null;
};

const _setUserDataToLocalStorage = (userData) => {
    localStorage.setItem('token', JSON.stringify(userData));
};

const _isUserLogged = () => {
    const userData = _getUserDataFromLocalStorage();
    return !!userData?.token;
};

const _updateIATToken = (userData) => {
    const { iat } = _parseJwt(userData.token);
    localStorage.setItem('iatToken', iat * 1000);
};

const UserDelivertyProvider = (props) => {
    const { children } = props;
    const router = useRouter();
    const [userData, setUserData] = useState({ ...defaultUserData });
    const [isEditModeEnable, setIsEditModeEnable] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [missatge, setMissatge] = useState();
    const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
    const REFRESH_TOKEN_TIME_MILISECONDS = 86400000; // 24H
    const isEditMode = isEditModeActive(router?.query);

    useEffect(() => {
        const getUserTokenFromUrlHash = () => {
            const hash = router.asPath.split('#')[1];
            if (hash) {
                const hashParams = new URLSearchParams(hash);
                const dtytoken = hashParams.get('dtytoken');
                if (dtytoken) {
                    const usuari = _parseJwt(dtytoken)?.sub || null;
                    const userData = { token: dtytoken, usuari: usuari };
                    _setUserDataToLocalStorage(userData);
                    const url = router.asPath.split('#')[0];
                    window.history.replaceState({}, '', url);
                }
            }
        };
        getUserTokenFromUrlHash();
    }, []);

    useEffect(() => {
        const removeUser = (error) => {
            localStorage.removeItem('token');
            localStorage.removeItem('iatToken');
            setIsEditModeEnable(false);
            setMissatge('ERROR REFRESCANT USUARI');
            setShowLogin(true);
            setLastRefreshTime(Date.now());
        };

        const refreshToken = async () => {
            const userData = _getUserDataFromLocalStorage();
            if (!userData) return null;

            const urlParams = new URLSearchParams(window.location.search);
            const isDelivertyBeta = urlParams.get('dty-beta') === 'true';
            const prefixApi = isDelivertyBeta ? window.jitConfig.apiDELIVERTYBeta : window.jitConfig.apiDELIVERTY;
            const pathApi = '/auth/refreshToken';
            const result = await fetch(`${prefixApi}${pathApi}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + userData.token
                }
            });
            if (result && result.status === 401) {
                removeUser();
            }
            const data = await result?.json();
            if (result === undefined || result.status !== 200) {
                removeUser(result.error);
            } else {
                const userData = _getUserDataFromLocalStorage();
                if (userData) {
                    userData.token = data.refreshToken;
                    _setUserDataToLocalStorage(userData);
                    _updateIATToken(userData);
                    setIsEditModeEnable(true);
                }
                setUserData(userData);
                setLastRefreshTime(Date.now());
            }
            return null;
        };

        const isLogged = _isUserLogged();
        if (isEditMode) {
            if (isLogged) {
                refreshToken();
            } else {
                setShowLogin(true);
                setLastRefreshTime(Date.now());
            }
        }
    }, [isEditMode]);

    const publicApi = useMemo(() => {
        const loginUser = async ({ user, clau }) => {
            const entorn = window?.jitConfig?.entorn;
            if (!isValidString(user) || ('aws_pro' === entorn && !isValidString(clau))) {
                setMissatge('Error, usuari o contrasenya incorrectes');
                setLastRefreshTime(Date.now());
                return;
            }
            const prefixApi = window.jitConfig.apiDELIVERTY;
            const pathApi = `/auth/login`;
            const dataLogin = { login: user, clau: clau };

            const response = await fetch(`${prefixApi}${pathApi}`, {
                method: 'POST',
                body: JSON.stringify(dataLogin),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();
            if (data?.token !== undefined && data?.usuari !== undefined) {
                _setUserDataToLocalStorage(data);
                setShowLogin(false);
                setUserData(data);
                setIsEditModeEnable(true);
            } else {
                setMissatge('Error, usuari o contrasenya incorrectes');
            }
            setLastRefreshTime(Date.now());
        };

        return {
            userData,
            loginUser,
            isEditModeEnable,
            lastRefreshTime
        };
    }, [userData, isEditModeEnable, lastRefreshTime]);

    return (
        <UserDelivertyContext.Provider value={publicApi}>
            {showLogin && <LoginDty missatge={missatge}></LoginDty>}
            {children}
        </UserDelivertyContext.Provider>
    );
};

export default UserDelivertyProvider;
